import { joinSurveyAction } from '../join-survey';
import type { ConnectChannel } from '../types';

/**
 * Static content for the Location channel detail page (`/connect/location`;
 * the old `/connect/brick-mortar-visits` redirects here). Same sourcing rule
 * as demographics.ts:
 * copied, not imported, since the source (Data Catalog / EXPLORE_CHANNELS)
 * backs internal-only tooling and this page is visible to every Home user.
 */
export const LOCATION_CHANNEL: ConnectChannel = {
    name: 'Location',
    icon: 'map-pin',
    color: '#418363',
    subtitle: 'US · 1,036 categories · 7,297 chains',
    subtitleLine2: 'Daily · Since 2015',
    exploreDaniPrompt: 'What can location visit data tell me about my consumers?',
    actions: [
        // Templates (src/@connect/prompt-template.ts): DANI asks one question
        // per `{slot|fallback}` before it runs (questions.ts). An unanswered
        // slot prints its fallback, the wording these prompts used before they
        // had slots. The ids are what the click sends to DANI (use-cases.ts).
        {
            id: 'location-track-visits',
            summary: "how visits to your stores trend week by week next to your competitors'",
            name: 'Track visits',
            icon: 'activity',
            template:
                'Weekly visit trend for {brand|my} stores vs. {competitors|the two nearest competitors} in {markets|all US markets} over {window|the last 12 weeks}',
        },
        {
            id: 'location-map-trade-areas',
            summary: 'where visitors to your best locations come from, and who they are',
            name: 'Map trade areas',
            icon: 'compass',
            template:
                'Map the trade area around the top-performing {brand|} locations in {markets|all US markets}, with shopper profiles',
        },
        {
            id: 'location-compare-competitors',
            summary: "how visits to your stores compare with your competitors'",
            name: 'Compare competitors',
            icon: 'eye',
            template:
                'Compare visit patterns between {brand|my} stores and {competitors|my closest competitors} in {markets|all US markets} over {window|the last 12 weeks}',
        },
        {
            id: 'location-dwell-time',
            summary: "how long visitors stay at your locations compared with your competitors'",
            name: 'Read dwell time',
            icon: 'clock',
            template:
                'How long do visitors dwell at {brand|my} locations vs. {competitors|competitors} in {markets|all US markets} over {window|the last 12 weeks}?',
        },
        joinSurveyAction('location'),
    ],
    schema: [
        { field: 'consumer_id', type: 'string', description: 'Pseudonymous panel ID' },
        { field: 'venue_name', type: 'string', description: 'Place name' },
        { field: 'chain', type: 'string', description: 'Parent brand (nullable)' },
        { field: 'category', type: 'string', description: 'Venue category' },
        { field: 'city', type: 'string', description: 'Visit city' },
        { field: 'state', type: 'string', description: '2-letter state code' },
        { field: 'visit_date', type: 'date', description: 'Visit date' },
        { field: 'dwell_minutes', type: 'float', description: 'Minutes at venue' },
        {
            field: 'time_of_day',
            type: 'string',
            description: 'Morning / Mid-Day / Evening / Late Night',
        },
    ],
    sampleCols: ['Venue', 'Chain', 'Category', 'City', 'State', 'Date', 'Dwell'],
    sampleColWidths: [
        'minmax(140px, 2fr)',
        'minmax(90px, 1fr)',
        'minmax(120px, 1.5fr)',
        'minmax(90px, 1fr)',
        '60px',
        '90px',
        '80px',
    ],
    sampleRows: [
        [
            "McDonald's",
            "McDonald's",
            'Fast Food Restaurant',
            'Irving',
            'TX',
            '2026-04-06',
            '2.1 min',
        ],
        ['Southridge Village', '—', 'Structure', 'Conway', 'AR', '2026-04-03', '31.0 min'],
        ['Dollar Tree', 'Dollar Tree', 'Discount Store', 'Akron', 'OH', '2026-03-30', '4.4 min'],
        [
            'Walmart Auto Care Centers',
            '—',
            'Fuel Station',
            'Universal City',
            'TX',
            '2026-03-26',
            '3.5 min',
        ],
        ['University Apartments', '—', 'Structure', 'Orlando', 'FL', '2026-04-03', '18.0 min'],
    ],
};
